import { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';

import AddSummaryModal from '@atom/components/common/workOrderDetail/workOrderSummary/AddSummaryModal';
import WorkOrderPreviewContext, {
  ExpandedInfo,
} from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { Icon, IconButton } from '@atom/mui';
import { AddIcon, ExpandLessIcon, ExpandMoreIcon } from '@atom/mui/icons';
import { Summary } from '@atom/types/work';
import api from '@atom/utilities/api';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import SummaryRow from './SummaryRow';

import classes from './infoView.module.css';

const SummarySection = () => {
  const { expandedInfo, setExpandedInfo, workOrderDetail, refetch } =
    useContext(WorkOrderPreviewContext);

  const [open, setOpen] = useState<boolean>(false);
  const [summary, setSummary] = useState<Summary[]>(
    workOrderDetail.summary || [],
  );

  useEffect(() => {
    setSummary(workOrderDetail.summary);
  }, [workOrderDetail.summary]);

  const updateSummary = async (updatedSummary: Summary[]) => {
    setSummary(updatedSummary);

    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}`;

    await api.patch(endpoint, { summary: updatedSummary });
    refetch();
  };

  const addSummary = (newSummary: Summary) => {
    updateSummary([...summary, newSummary]);
  };

  const toggleSection = () => {
    setExpandedInfo(toggleFromSet(expandedInfo, ExpandedInfo.SUMMARY));
  };

  const icon = expandedInfo.has(ExpandedInfo.SUMMARY)
    ? ExpandLessIcon
    : ExpandMoreIcon;

  const canAddSummary = hasRolePermissions(ROLE_SETS.INSPECTOR);

  return (
    <>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitle}>Summary</div>
        <div>
          {canAddSummary && (
            <IconButton onClick={() => setOpen(true)}>
              <Icon icon={AddIcon} />
            </IconButton>
          )}
          <IconButton onClick={toggleSection}>
            <Icon icon={icon} />
          </IconButton>
        </div>
      </div>
      {expandedInfo.has(ExpandedInfo.SUMMARY) && (
        <div className={classes.sectionContent}>
          {R.isEmpty(summary) ? (
            <div className={classes.noSummary}>There is no summary.</div>
          ) : (
            summary.map((summaryItem: Summary, index: number) => (
              <SummaryRow
                key={`${summaryItem.title}-${index}`}
                summary={summaryItem}
                summaries={summary}
                index={index}
                updateSummary={updateSummary}
              />
            ))
          )}
        </div>
      )}
      <AddSummaryModal
        open={open}
        onClose={() => setOpen(false)}
        addSummary={addSummary}
      />
    </>
  );
};

export default SummarySection;
